import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { setQuietHoursPrefs } from '../utils/quietHours';

const FeatureFlagsContext = createContext(null);

// Map notification feature names to user setting keys (static — hoisted to module
// scope so it's a stable reference and not a hook dependency).
const NOTIF_FEATURE_TO_PREF = {
  notification_email: 'email_enabled',
  notification_sms: 'sms_enabled',
  notification_push: 'push_enabled',
};

export const useFeatureFlags = () => {
  const context = useContext(FeatureFlagsContext);
  if (!context) {
    throw new Error('useFeatureFlags must be used within a FeatureFlagsProvider');
  }
  return context;
};

export const FeatureFlagsProvider = ({ children }) => {
  const { api, isAuthenticated, user } = useAuth();
  const [features, setFeatures] = useState({
    pages: {},
    subpages: {},
    modules: {},
    map_providers: { osm_maps: true, google_maps: false },
    map_features: { 
      map_satellite_view: false, 
      map_street_view: false, 
      map_traffic_layer: false, 
      map_terrain_view: false 
    },
    device_actions: {},
    integrations: {},
    landing_page: 'dashboard_page',
    call_limits: { voice_max: 0, video_max: 0 }
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // User's personal notification preferences (from Settings > Notifications)
  const [notificationPrefs, setNotificationPrefs] = useState({
    email_enabled: true,
    sms_enabled: true,
    push_enabled: true,
  });
  
  // Check if user is admin
  const isAdmin = user?.role === 'super_admin' || user?.role === 'admin' || user?.role === 'manager';

  const fetchFeatures = useCallback(async () => {
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }

    try {
      const [featuresRes, notifRes] = await Promise.all([
        api.get('/features'),
        api.get('/settings/notifications').catch(() => null),
      ]);
      setFeatures(featuresRes.data);
      if (notifRes?.data) {
        setNotificationPrefs({
          email_enabled: notifRes.data.email_enabled ?? true,
          sms_enabled: notifRes.data.sms_enabled ?? true,
          push_enabled: notifRes.data.push_enabled ?? true,
        });
        setQuietHoursPrefs(notifRes.data);
      }
      setError(null);
    } catch (err) {
      console.error('Failed to fetch feature flags:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [api, isAuthenticated]);

  useEffect(() => {
    fetchFeatures();
  }, [fetchFeatures]);

  // Check if a specific feature is enabled
  const isFeatureEnabled = useCallback((featureName) => {
    // Check all categories
    const allFeatures = {
      ...features.pages,
      ...features.subpages,
      ...features.modules,
      ...features.map_providers,
      ...features.map_features,
      ...features.device_actions,
      ...features.integrations
    };
    return allFeatures[featureName] ?? false;
  }, [features]);

  // Check if a page is enabled
  const isPageEnabled = useCallback((pageName) => {
    // If the page exists in features, use its value; otherwise default to false
    if (pageName in features.pages) {
      return features.pages[pageName];
    }
    return false; // Default to false for unlisted pages
  }, [features.pages]);

  // Check if a subpage is enabled
  const isSubpageEnabled = useCallback((subpageName) => {
    if (['live_tracking_staff', 'live_tracking_passengers', 'live_tracking_assets'].includes(subpageName)) {
      return features.subpages[subpageName] ?? isPageEnabled('live_tracking_page');
    }
    // Super Admin gets all subpages by default
    if (user?.role === 'super_admin') {
      return features.subpages[subpageName] ?? true;
    }
    // Admin and Manager must have explicit permission from backend
    // The backend controls which subpages each role can access
    return features.subpages[subpageName] ?? false;
  }, [features.subpages, isPageEnabled, user?.role]);

  // Check if a map provider is enabled
  const isMapProviderEnabled = useCallback((providerName) => {
    return features.map_providers[providerName] ?? false;
  }, [features.map_providers]);

  // Check if a map feature is enabled
  const isMapFeatureEnabled = useCallback((featureName) => {
    // Map features also require Google Maps to be enabled
    const googleMapsEnabled = features.map_providers.google_maps ?? false;
    const featureEnabled = features.map_features[featureName] ?? false;
    return googleMapsEnabled && featureEnabled;
  }, [features.map_providers, features.map_features]);

  // Check if a device action is enabled
  const isDeviceActionEnabled = useCallback((actionName) => {
    return features.device_actions[actionName] ?? false;
  }, [features.device_actions]);

  // Check if a module is enabled
  const isModuleEnabled = useCallback((moduleName) => {
    return features.modules[moduleName] ?? false;
  }, [features.modules]);

  // Check if OSM Maps is available based on privileges
  // OSM is available if: explicitly enabled (true) OR if nothing is enabled (fallback)
  const isOSMMapsAvailable = useCallback(() => {
    const osmEnabled = features.map_providers.osm_maps === true;
    const googleEnabled = features.map_providers.google_maps === true;
    // OSM available if explicitly enabled, OR if neither is enabled (fallback to OSM)
    return osmEnabled || (!osmEnabled && !googleEnabled);
  }, [features.map_providers]);

  // Check if Google Maps is available (provider enabled + has API key)
  const isGoogleMapsAvailable = useCallback(() => {
    const hasApiKey = !!process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
    const isEnabled = features.map_providers.google_maps === true;
    return hasApiKey && isEnabled;
  }, [features.map_providers]);

  // Get available map layers based on enabled features
  // Logic:
  // - OSM ❌ + Google ❌ = Show OSM only (fallback)
  // - OSM ✅ + Google ❌ = Show OSM only
  // - OSM ✅ + Google ✅ = Show both OSM & Google
  // - OSM ❌ + Google ✅ = Show Google only (OSM hidden)
  const getAvailableMapLayers = useCallback(() => {
    const layers = [];
    const osmEnabled = features.map_providers.osm_maps === true;
    const googleEnabled = isGoogleMapsAvailable();
    
    // Add OSM if enabled OR if nothing is enabled (fallback)
    if (osmEnabled || (!osmEnabled && !googleEnabled)) {
      layers.push({ id: 'osm', name: 'OpenStreetMap', type: 'osm' });
    }
    
    // Add Google Maps layers if enabled
    if (googleEnabled) {
      layers.push({ id: 'google_roadmap', name: 'Google Maps', type: 'google', mapType: 'roadmap' });
      
      if (features.map_features.map_satellite_view) {
        layers.push({ id: 'google_satellite', name: 'Satellite', type: 'google', mapType: 'satellite' });
        layers.push({ id: 'google_hybrid', name: 'Hybrid', type: 'google', mapType: 'hybrid' });
      }
      
      if (features.map_features.map_terrain_view) {
        layers.push({ id: 'google_terrain', name: 'Terrain', type: 'google', mapType: 'terrain' });
      }
    }
    
    return layers;
  }, [features.map_providers, features.map_features, isGoogleMapsAvailable]);

  // Get enabled device actions
  const getEnabledDeviceActions = useCallback(() => {
    return Object.entries(features.device_actions)
      .filter(([_, enabled]) => enabled)
      .map(([name]) => name);
  }, [features.device_actions]);

  // Check if a notification channel is fully active (feature flag + privilege + user setting)
  const isNotificationChannelActive = useCallback((featureName) => {
    const allFeatures = {
      ...features.pages,
      ...features.subpages,
      ...features.modules,
      ...features.map_providers,
      ...features.map_features,
      ...features.device_actions,
      ...features.integrations
    };
    const featureEnabled = allFeatures[featureName] ?? false;
    if (!featureEnabled) return false;
    const prefKey = NOTIF_FEATURE_TO_PREF[featureName];
    if (prefKey) return notificationPrefs[prefKey] !== false;
    return featureEnabled;
  }, [features, notificationPrefs]);

  // Get the reason why a notification channel is disabled
  const getNotificationDisabledReason = useCallback((featureName) => {
    const allFeatures = {
      ...features.pages,
      ...features.subpages,
      ...features.modules,
      ...features.map_providers,
      ...features.map_features,
      ...features.device_actions,
      ...features.integrations
    };
    const featureEnabled = allFeatures[featureName] ?? false;
    if (!featureEnabled) return 'privilege';
    const prefKey = NOTIF_FEATURE_TO_PREF[featureName];
    if (prefKey && notificationPrefs[prefKey] === false) return 'user_setting';
    return null;
  }, [features, notificationPrefs]);

  const value = {
    features,
    loading,
    error,
    refetch: fetchFeatures,
    isFeatureEnabled,
    isNotificationChannelActive,
    getNotificationDisabledReason,
    notificationPrefs,
    setNotificationPrefs,
    isPageEnabled,
    isSubpageEnabled,
    isMapProviderEnabled,
    isMapFeatureEnabled,
    isDeviceActionEnabled,
    isModuleEnabled,
    isOSMMapsAvailable,
    isGoogleMapsAvailable,
    getAvailableMapLayers,
    getEnabledDeviceActions,
    // Shortcuts for common checks
    hasOSMMaps: isOSMMapsAvailable(),
    hasGoogleMaps: isGoogleMapsAvailable(),
    hasSatelliteView: isMapFeatureEnabled('map_satellite_view'),
    hasStreetView: isMapFeatureEnabled('map_street_view'),
    hasTrafficLayer: isMapFeatureEnabled('map_traffic_layer'),
    hasTerrainView: isMapFeatureEnabled('map_terrain_view'),
    hasHeatmap: isMapFeatureEnabled('map_heatmap'),
    hasWeather: isMapFeatureEnabled('map_weather'),
    hasNearestDevice: isMapFeatureEnabled('map_nearest_device'),
    hasDarkMode: isMapFeatureEnabled('map_dark_mode'),
    hasGeofenceOverlay: isMapFeatureEnabled('map_geofence_overlay'),
  };

  return (
    <FeatureFlagsContext.Provider value={value}>
      {children}
    </FeatureFlagsContext.Provider>
  );
};

export default FeatureFlagsContext;
